// Migrated to ES6 class syntax for ol@10 compat.
// Changes:
// - removed inherits() from ol/index.js → ES6 extends
// - removed LayerType.TILE (não existe mais em ol@6+); o renderer agora é
//   atribuído explicitamente via createRenderer() retornando CanvasTileLayerRenderer
// - TileProperty.PRELOAD / USE_INTERIM_TILES_ON_ERROR (removidos) → strings literais
// - assign() → Object.assign()
import Layer from 'ol/layer/Layer.js';
import CanvasTileLayerRenderer from 'ol/renderer/canvas/TileLayer.js';

const PROP_PRELOAD = 'preload';
const PROP_USE_INTERIM_TILES_ON_ERROR = 'useInterimTilesOnError';

/**
 * Layer customizado pra sources de tile que herdam de ol/source/Tile (como
 * GoogleSource, SelectionLayerTileSource, FeatureLayerWorkerTileSource).
 *
 * Em ol@5 a escolha do renderer era automática via LayerType.TILE. Em ol@10
 * precisamos retornar o renderer explicitamente em createRenderer().
 *
 * @extends {Layer}
 */
class CanvasLayer extends Layer {
  /**
   * @param {object=} opt_options Tile layer options.
   */
  constructor(opt_options) {
    var options = opt_options ? opt_options : {};

    var baseOptions = Object.assign({}, options);
    delete baseOptions.preload;
    delete baseOptions.useInterimTilesOnError;

    super(baseOptions);

    this.setPreload(options.preload !== undefined ? options.preload : 0);
    this.setUseInterimTilesOnError(
      options.useInterimTilesOnError !== undefined ? options.useInterimTilesOnError : true,
    );

    this.on('change:visible', () => this.handleVisibilityChange());

    var source = this.getSource();
    if (source) {
      source.on('clear', () => this.handleSourceClear());
    }
  }

  /**
   * Atribui o renderer canvas pra tiles. Substitui o lookup automático
   * por LayerType que existia em ol@5.
   */
  createRenderer() {
    return new CanvasTileLayerRenderer(this);
  }

  /**
   * @return {number} Preload level.
   */
  getPreload() {
    return /** @type {number} */ (this.get(PROP_PRELOAD));
  }

  /**
   * @param {number} preload Preload level.
   */
  setPreload(preload) {
    this.set(PROP_PRELOAD, preload);
  }

  /**
   * @return {boolean} Use interim tiles on error.
   */
  getUseInterimTilesOnError() {
    return /** @type {boolean} */ (this.get(PROP_USE_INTERIM_TILES_ON_ERROR));
  }

  /**
   * @param {boolean} useInterimTilesOnError
   */
  setUseInterimTilesOnError(useInterimTilesOnError) {
    this.set(PROP_USE_INTERIM_TILES_ON_ERROR, useInterimTilesOnError);
  }

  /**
   * Propaga visibility do layer pro source (sources customizados usam pra
   * pausar workers etc.).
   * @private
   */
  handleVisibilityChange() {
    var source = this.getSource();
    if (source && typeof source.setVisibility === 'function') {
      source.setVisibility(this.getVisible());
    }
  }

  /**
   * Desliga useInterimTilesOnError temporariamente quando o source sinaliza
   * clear, pra forçar re-render dos tiles novos.
   * @private
   */
  handleSourceClear() {
    if (!this.getUseInterimTilesOnError())
      return;

    this.setUseInterimTilesOnError(false);

    setTimeout(() => {
      if (!this.getUseInterimTilesOnError())
        this.setUseInterimTilesOnError(true);
    }, 200);
  }
}

export default CanvasLayer;
